import React from 'react';
import { BackIcon, WhatsAppFAB, formatPrice } from './catalogueHelpers';

const ProductView = ({
  selectedProduct,
  slug,
  businessName, accentColor, settings,
  products, loadCatalogue,
  canGoBack,
  cartEnabled, cartCount, addToCart,
  productQuantity, setProductQuantity,
  selectedVariant, setSelectedVariant,
  selectedOptions, setSelectedOptions,
  activeImageIndex, setActiveImageIndex,
  setBuyNowItems, setView,
  setSelectedProduct, setSearchOpen, setSearchQuery,
}) => {
  const images = selectedProduct.images || [];
  const activeImage = images[activeImageIndex]?.url || null;
  const basePrice = selectedVariant?.price ?? selectedProduct.price;
  const optionsTotal = (selectedOptions ?? []).reduce((s, o) => s + (o.price ?? 0), 0);
  const effectivePrice = basePrice != null ? basePrice + optionsTotal : null;
  const totalPrice = effectivePrice != null ? effectivePrice * productQuantity : null;

  const isChoiceSelected = (groupLabel, choice) =>
    (selectedOptions ?? []).some(o => o.groupLabel === groupLabel && o.name === choice.name);

  const getChoiceQty = (groupLabel, choiceName) => {
    const found = (selectedOptions ?? []).find(o => o.groupLabel === groupLabel && o.name === choiceName);
    return found ? (found.qty ?? 1) : 0;
  };

  const toggleChoice = (group, choice) => {
    const current = selectedOptions ?? [];
    const alreadySelected = current.some(o => o.groupLabel === group.label && o.name === choice.name);
    const withoutGroup = current.filter(o => o.groupLabel !== group.label);
    if (alreadySelected && !group.required) {
      setSelectedOptions(withoutGroup);
    } else {
      setSelectedOptions([...withoutGroup, { groupLabel: group.label, name: choice.name, price: choice.price, qty: 1 }]);
    }
  };

  const adjustChoiceQty = (group, choice, delta) => {
    const current = selectedOptions ?? [];
    const existing = current.find(o => o.groupLabel === group.label && o.name === choice.name);
    const currentQty = existing ? (existing.qty ?? 1) : 0;
    const newQty = Math.max(0, currentQty + delta);
    if (newQty === 0) {
      setSelectedOptions(current.filter(o => !(o.groupLabel === group.label && o.name === choice.name)));
    } else if (existing) {
      setSelectedOptions(current.map(o =>
        o.groupLabel === group.label && o.name === choice.name ? { ...o, qty: newQty } : o
      ));
    } else {
      setSelectedOptions([...current, { groupLabel: group.label, name: choice.name, price: choice.price, qty: newQty }]);
    }
  };

  // A required group is satisfied when at least one of its choices is selected.
  const missingRequiredGroups = (selectedProduct.options ?? [])
    .filter(g => g.required && !(selectedOptions ?? []).some(o => o.groupLabel === g.label));
  const canBuy = missingRequiredGroups.length === 0;

  const buyNow = () => {
    setBuyNowItems([{ product: selectedProduct, quantity: productQuantity, variant: selectedVariant, options: selectedOptions ?? [] }]);
    setView('checkout');
  };

  const handleBack = () => {
    if (products.length === 0) loadCatalogue();
    setView('catalogue');
    setSelectedProduct(null);
    setProductQuantity(1);
    setSelectedVariant(null);
    setSelectedOptions([]);
    setActiveImageIndex(0);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white px-4 sticky top-0 z-10">
        <div className="max-w-3xl lg:max-w-6xl mx-auto flex items-center justify-between gap-3 py-4 border-b border-gray-200">
          <div className="flex items-center gap-1 min-w-0">
            {canGoBack && (
              <button onClick={handleBack} className="p-2 -ml-2 text-gray-500 hover:text-gray-900 shrink-0">
                <BackIcon />
              </button>
            )}
            <a href={`/c/${slug}`} className="text-lg font-bold text-gray-900 hover:no-underline truncate">{businessName}</a>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={() => { if (products.length === 0) loadCatalogue(); setView('catalogue'); setProductQuantity(1); setSelectedVariant(null); setSelectedOptions([]); setActiveImageIndex(0); setSearchOpen(true); setSearchQuery(''); }}
              className="p-2 rounded-full text-gray-600 hover:bg-gray-100 transition"
            >
              <svg fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" width="22" height="22">
                <circle cx="11" cy="11" r="8" /><path strokeLinecap="round" d="M21 21l-4.35-4.35" />
              </svg>
            </button>
            {cartEnabled && (
              <button
                onClick={() => cartCount > 0 && setView('checkout')}
                className="relative p-2 rounded-full text-gray-900 hover:bg-gray-100 transition"
              >
                <svg aria-hidden="true" fill="currentColor" height="22" viewBox="0 0 24 24" width="22" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M7.309 6.25a4.751 4.751 0 0 1 9.382 0H18.5a1.25 1.25 0 0 1 1.25 1.25v11a3.25 3.25 0 0 1-3.25 3.25h-9a3.25 3.25 0 0 1-3.25-3.25v-11A1.25 1.25 0 0 1 5.5 6.25h1.809m1.528 0a3.251 3.251 0 0 1 6.326 0H8.837M5.75 18.5V7.75h12.5V18.5a1.75 1.75 0 0 1-1.75 1.75h-9a1.75 1.75 0 0 1-1.75-1.75" clipRule="evenodd" />
                </svg>
                {cartCount > 0 && (
                  <span
                    className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full text-white text-[10px] font-bold flex items-center justify-center px-1"
                    style={accentColor ? { backgroundColor: accentColor } : { backgroundColor: '#F89133' }}
                  >
                    {cartCount}
                  </span>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">

          {/* Images */}
          <div className="space-y-3">
            <div className="rounded-2xl overflow-hidden bg-gray-100 aspect-square flex items-center justify-center">
              {activeImage ? (
                <img src={activeImage} alt={selectedProduct.name} className="w-full h-full object-cover" />
              ) : (
                <svg width="56" height="56" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="3" width="18" height="18" rx="2" stroke="#d1d5db" strokeWidth="1.5"/>
                  <circle cx="8.5" cy="8.5" r="1.5" stroke="#d1d5db" strokeWidth="1.5"/>
                  <path d="M3 15l5-5 4 4 3-3 6 6" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </div>
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-1">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImageIndex(i)}
                    className="shrink-0 w-16 h-16 rounded-xl overflow-hidden border-2 transition-colors"
                    style={{ borderColor: i === activeImageIndex ? (accentColor || '#F89133') : 'transparent' }}
                  >
                    <img src={img.url} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="flex flex-col gap-6">
            <div className="border-b border-gray-100 pb-6">
              <h1 className="text-xl font-medium text-gray-900">{selectedProduct.name}</h1>
              {effectivePrice != null && (
                <p className="text-xl font-bold text-gray-900 mt-2">{formatPrice(effectivePrice)}</p>
              )}
              {selectedProduct.description && (
                <p className="text-gray-500 leading-relaxed text-sm mt-4">{selectedProduct.description}</p>
              )}
              {selectedProduct.variants?.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-600 mb-2">Variante</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedProduct.variants.map((v, i) => {
                      const isSelected = selectedVariant?.name === v.name;
                      return (
                        <button
                          key={i}
                          onClick={() => setSelectedVariant(isSelected ? null : v)}
                          className="px-3 py-1.5 rounded-lg border text-sm font-medium transition-all"
                          style={isSelected ? {
                            backgroundColor: accentColor || '#111111',
                            borderColor: accentColor || '#111111',
                            color: 'white',
                          } : {
                            backgroundColor: 'white',
                            borderColor: '#e5e7eb',
                            color: '#374151',
                          }}
                        >
                          {v.name}{v.price != null ? ` (${formatPrice(v.price)})` : ''}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {(selectedProduct.options ?? []).map((group, gi) => (
                <div key={gi} className="mt-4">
                  <div className="flex items-baseline justify-between mb-2">
                    <p className="text-sm font-medium text-gray-700">
                      {group.label}
                      {group.required && <span className="text-red-500 ml-1">*</span>}
                    </p>
                    <p className="text-xs text-gray-400">
                      {group.type === 'single' ? 'Un seul' : 'Multi-choix'}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1">
                    {(group.choices ?? []).map((choice, ci) => {
                      if (group.type === 'single') {
                        const isSelected = isChoiceSelected(group.label, choice);
                        return (
                          <button
                            key={ci}
                            type="button"
                            onClick={() => toggleChoice(group, choice)}
                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border text-sm transition-all"
                            style={{
                              backgroundColor: 'white',
                              borderColor: isSelected ? (accentColor || '#111111') : '#e5e7eb',
                              color: '#111111',
                            }}
                          >
                            <span
                              className="inline-flex items-center justify-center shrink-0"
                              style={{
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                border: `2px solid ${isSelected ? (accentColor || '#111111') : '#d1d5db'}`,
                                backgroundColor: isSelected ? (accentColor || '#111111') : 'white',
                              }}
                            >
                              {isSelected && <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'white' }} />}
                            </span>
                            <span className="flex-1 text-left font-medium">{choice.name}</span>
                            {choice.price != null && choice.price > 0 && (
                              <span className="text-sm text-gray-600 shrink-0">{formatPrice(choice.price)}</span>
                            )}
                          </button>
                        );
                      }

                      // Multiple — qty stepper
                      const qty = getChoiceQty(group.label, choice.name);
                      return (
                        <div
                          key={ci}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border text-sm transition-all"
                          style={{ borderColor: qty > 0 ? (accentColor || '#111111') : '#e5e7eb' }}
                        >
                          <span className="flex-1 font-medium text-gray-900">{choice.name}</span>
                          {choice.price != null && choice.price > 0 && (
                            <span className="text-sm text-gray-500 shrink-0">{formatPrice(choice.price)}</span>
                          )}
                          <div className="flex items-center shrink-0">
                            <button
                              type="button"
                              onClick={() => adjustChoiceQty(group, choice, -1)}
                              className="flex items-center justify-center rounded-lg text-base font-medium transition-all duration-200"
                              style={{
                                width: qty > 0 ? '1.75rem' : 0,
                                height: '1.75rem',
                                opacity: qty > 0 ? 1 : 0,
                                overflow: 'hidden',
                                pointerEvents: qty > 0 ? 'auto' : 'none',
                                backgroundColor: '#f3f4f6',
                                color: '#374151',
                                padding: 0,
                                minWidth: 0,
                                marginRight: qty > 0 ? 6 : 0,
                              }}
                            >−</button>
                            <span
                              className="text-center text-sm font-semibold text-gray-900 transition-all duration-200"
                              style={{
                                width: qty > 0 ? '1.25rem' : 0,
                                opacity: qty > 0 ? 1 : 0,
                                overflow: 'hidden',
                                display: 'inline-block',
                                marginRight: qty > 0 ? 6 : 0,
                              }}
                            >{qty}</span>
                            <button
                              type="button"
                              onClick={() => adjustChoiceQty(group, choice, +1)}
                              className="w-7 h-7 rounded-lg flex items-center justify-center text-base font-medium transition"
                              style={{ backgroundColor: accentColor || '#111111', color: 'white' }}
                            >+</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between">
              <span className="font-semibold text-gray-900">Quantité</span>
              <div className="flex items-center border border-gray-200 rounded-xl overflow-hidden">
                <button onClick={() => setProductQuantity(q => Math.max(1, q - 1))} className="w-11 h-11 flex items-center justify-center text-gray-600 hover:bg-gray-50 text-lg transition">−</button>
                <span className="w-10 text-center font-semibold text-gray-900">{productQuantity}</span>
                <button onClick={() => setProductQuantity(q => q + 1)} className="w-11 h-11 flex items-center justify-center text-gray-600 hover:bg-gray-50 text-lg transition">+</button>
              </div>
            </div>

            {/* Desktop CTA */}
            <div className="hidden lg:flex flex-col gap-3">
              {!canBuy && (
                <p className="text-xs text-red-500 -mb-1">
                  Veuillez choisir : {missingRequiredGroups.map(g => g.label).join(', ')}
                </p>
              )}
              <button
                onClick={canBuy ? buyNow : undefined}
                disabled={!canBuy}
                className="w-full py-4 rounded-xl text-white font-bold text-base flex items-center justify-center gap-3 transition active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
                style={{ backgroundColor: accentColor || '#111111' }}
              >
                <span>Acheter maintenant</span>
                {totalPrice != null && <span className="opacity-70 font-medium text-sm">{formatPrice(totalPrice)}</span>}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Powered by */}
      <div className="text-center py-8 text-xs text-gray-400">
        Propulsé par{' '}
        <a href="https://ouigoo.co" target="_blank" rel="noopener noreferrer" className="font-semibold text-gray-500 hover:text-gray-700 transition-colors">
          OuiGoo
        </a>
      </div>

      {/* Mobile sticky CTA */}
      <>
        <div className="h-20 lg:hidden" />
        <div className="lg:hidden fixed bottom-0 left-0 right-0 px-4 py-3 bg-white border-t border-gray-100 shadow-lg">
          {!canBuy && (
            <p className="text-xs text-red-500 mb-2 text-center">
              Veuillez choisir : {missingRequiredGroups.map(g => g.label).join(', ')}
            </p>
          )}
          <button
            onClick={canBuy ? buyNow : undefined}
            disabled={!canBuy}
            className="w-full py-4 rounded-xl text-white font-bold text-base flex items-center justify-center gap-3 transition active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
            style={{ backgroundColor: accentColor || '#111111' }}
          >
            <span>Acheter maintenant</span>
            {totalPrice != null && <span className="opacity-70 font-medium text-sm">{formatPrice(totalPrice)}</span>}
          </button>
        </div>
      </>

      {settings.whatsappEnabled && settings.whatsappNumber && (
        <WhatsAppFAB
          number={settings.whatsappNumber}
          message={`Bonjour, je suis intéressé(e) par : ${selectedProduct.name}`}
          bottomOffset="bottom-24 lg:bottom-6"
        />
      )}
    </div>
  );
};

export default ProductView;
